import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AISuggest } from '@/components/brand-bible';
import { proposalService } from '@/services/proposalService';
import { useTenantStore } from '@/stores/useTenantStore';
import type { BrandMap, CpcMode, MetaMode, Proposal, ReportInput } from '@/types/proposal';

// Default theme = Agile & Co brand (black + Accent Blue on white) — also the
// manual-picker fallback.
const DEFAULT_PRIMARY = '#000000';
const DEFAULT_ACCENT = '#4cc9f0';

// Named color-scheme presets (primary = structural, accent = highlight).
// "Agile & Co" is the agency's brand: black + Accent Blue (#4CC9F0) on white,
// blue used sparingly as an accent — never a large fill.
const COLOR_PRESETS: { name: string; primary: string; accent: string }[] = [
  { name: 'Agile & Co', primary: '#000000', accent: '#4cc9f0' },
  { name: 'Classic', primary: '#1b2a48', accent: '#c0562b' },
  { name: 'Blue', primary: '#0b3b6b', accent: '#f2ae30' },
  { name: 'Forest', primary: '#14432a', accent: '#e0a615' },
  { name: 'Slate', primary: '#1f2937', accent: '#0ea5e9' },
  { name: 'Plum', primary: '#3b1d4e', accent: '#e0862b' },
];

// Report font presets (keys map to server-side stacks).
const FONT_OPTIONS: { key: string; label: string }[] = [
  { key: 'agileco', label: 'Agile & Co (Inter)' },
  { key: 'sans', label: 'Sans (Helvetica)' },
  { key: 'serif', label: 'Serif (Georgia)' },
  { key: 'modern', label: 'Modern (system)' },
  { key: 'grotesk', label: 'Grotesk (Trebuchet)' },
  { key: 'editorial', label: 'Editorial (serif + sans)' },
];

/**
 * Audit → Proposal (Stage 0 intake → Stage 7 render). Operator fills intake +
 * benchmark assumptions + Gate-B narrative, picks the CPC / Meta modes, and gets
 * the branded report (backend-rendered HTML) previewed in an iframe → print to PDF.
 * Coral+Teal tokens only.
 */
// Shared control styling. min-w-0 on `field`: inputs have an intrinsic min-width —
// without it, two-column rows refuse to shrink and force the pane to overflow.
const field =
  'mt-1 w-full min-w-0 rounded-lg border border-warm-border bg-paper-2 px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-coral focus:bg-paper focus:ring-2 focus:ring-ring/40';
const label = 'block text-[11px] font-medium text-muted-foreground';
// Studio card + micro-legend — reused for every control group.
const card = 'min-w-0 rounded-xl border border-warm-border bg-paper p-4 shadow-sm';
const legendCls = 'px-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground';

// Collapsed/expanded state persists per-user across visits. Sections default to
// open; once someone collapses one, that choice is remembered next time.
const SECTIONS_STORAGE_KEY = 'proposal-studio-sections'; // { [id]: open }

function readSectionState(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(SECTIONS_STORAGE_KEY) || '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}

function writeSectionState(id: string, open: boolean): void {
  try {
    const state = readSectionState();
    state[id] = open;
    localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode / quota — collapse state just won't persist */
  }
}

// Collapsible studio section: the legend is a toggle button with a chevron.
// Defined at module scope so it never remounts on the page's re-renders (which
// would drop input focus/selection while typing in a field).
function Section({
  id,
  title,
  defaultOpen = true,
  children,
}: {
  id: string;
  title: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(() => {
    const saved = readSectionState();
    return id in saved ? saved[id] : defaultOpen;
  });
  const toggle = () =>
    setOpen((o) => {
      const next = !o;
      writeSectionState(id, next);
      return next;
    });
  return (
    <div className={card}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={`-m-1 flex w-[calc(100%+0.5rem)] items-center gap-1.5 rounded-md p-1 cursor-pointer select-none transition-colors hover:text-foreground ${legendCls}`}
      >
        <svg
          viewBox="0 0 24 24"
          className={`h-3 w-3 shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
        {title}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

export default function ProposalsPage() {
  const [title, setTitle] = useState('Roofing (Full-Service) | Birmingham, Alabama Metro');
  const [subtitle, setSubtitle] = useState('Google Ads Budget & Forecast Recommendation · 20 July 2026');
  const [budget, setBudget] = useState('12000');
  const [cpc, setCpc] = useState('15, 16, 17, 20');
  const [cvr, setCvr] = useState('5');
  const [closeRate, setCloseRate] = useState('20');
  const [jobValue, setJobValue] = useState('7500');

  // Brand-tailoring (Stage-0): auto-extract the prospect's palette from their site.
  const [website, setWebsite] = useState('mikehammermoving.com');
  const [logoLabel, setLogoLabel] = useState('PROSPECT');
  const [logoUrl, setLogoUrl] = useState(''); // prospect logo (auto-filled on Fetch, or paste)
  const [brand, setBrand] = useState<BrandMap | null>(null);
  const [swatches, setSwatches] = useState<string[]>([]);
  const [brandLoading, setBrandLoading] = useState(false);
  // Manual override: which role the next swatch click assigns, + the raw picks.
  const [assignRole, setAssignRole] = useState<'primary' | 'accent'>('primary');
  const [primaryHex, setPrimaryHex] = useState<string | null>(null);
  const [accentHex, setAccentHex] = useState<string | null>(null);
  // Report mode + page/PDF background color + font.
  const [mode, setMode] = useState<'light' | 'dark'>('dark'); // Agile & Co default
  const [paper, setPaper] = useState('');
  const [font, setFont] = useState('agileco'); // Agile & Co brand font (Inter) by default
  const [editing, setEditing] = useState(false);

  const [cpcMode, setCpcMode] = useState<CpcMode>('manual');
  const [planningCpcs, setPlanningCpcs] = useState('17.5, 16, 15');
  const [cpcBase, setCpcBase] = useState('20');
  const [cpcDropRate, setCpcDropRate] = useState('10');
  const [metaMode, setMetaMode] = useState<MetaMode>('in_plan');

  const [readText, setReadText] = useState(
    'The nine target ZIPs are the affluent over-the-mountain suburbs of Birmingham. High home values, aging asphalt roofs, and a hard spring storm season make this one of the strongest residential roofing demand pockets in the state — high-intent, weather-driven demand that belongs to search first.',
  );
  const [recommendationText, setRecommendationText] = useState(
    'Start at Moderate. It funds Search + LSA + light Meta retargeting across the core clusters. Graduate to Aggressive for spring storm season once close rate and cost per job are proven in-account.',
  );

  const [includeAudit, setIncludeAudit] = useState(false);
  const [auditIntro, setAuditIntro] = useState(
    'We pulled the account live and reviewed the Local Services profile end to end. The picture is clear and, importantly, fixable.',
  );
  const [auditRows, setAuditRows] = useState(
    'Account structure: LSA-only shadow account — one Local Services campaign, no Search / PMax / Display.\nDelivery (Apr 1 – Jul 21): 637 impressions, 2 charged leads, $157 spend (~$79/lead); stalled the last 30 days.\nActive block: an unlicensed "Painter" industry failed LSA Minimum Provider Requirements and drags account health.\nReviews & reach: only 11 Google reviews; LSA is excluded from the Ads Transparency Center, so nothing shows there.',
  );
  const [auditDiagnosis, setAuditDiagnosis] = useState(
    'The account is not broken and not primarily a reviews problem — it is an LSA-only shadow account confined to a thin corner of demand, with a fixable compliance flag on top. The scalable demand needs Google Search and Performance Max, which do not exist today. That gap is the entire opportunity.',
  );

  // AI-suggest: drafted quick-fact tiles + pill live in state (no manual editor yet).
  const [tiles, setTiles] = useState<{ k: string; v: string }[]>([]);
  const [recPill, setRecPill] = useState('');
  const [suggesting, setSuggesting] = useState<string | null>(null);
  // Grounding pre-warm (fired when the website is entered).
  const [warmState, setWarmState] = useState<'idle' | 'warming' | 'ready' | 'error'>('idle');
  const [warmInfo, setWarmInfo] = useState<{ keywords: number; campaigns: number } | null>(null);
  const warmedKeyRef = useRef('');

  const [reportHtml, setReportHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Report-view mode: /proposals/:proposalId loads a saved report (e.g. one Emjae
  // generated in chat) into the preview so it can be reviewed + downloaded.
  const { proposalId } = useParams<{ proposalId?: string }>();
  const currentTenant = useTenantStore((s) => s.currentTenant);
  useEffect(() => {
    if (!proposalId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    proposalService
      .getProposalReport(proposalId, currentTenant || '')
      .then((html) => { if (!cancelled && html) setReportHtml(html); })
      .catch(() => { if (!cancelled) setError('Could not load that saved report.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [proposalId, currentTenant]);

  // History: saved proposals for the workspace (Emjae-generated), newest first.
  const navigate = useNavigate();
  const [history, setHistory] = useState<Proposal[]>([]);
  useEffect(() => {
    if (!currentTenant) return;
    let cancelled = false;
    proposalService
      .listProposals(currentTenant)
      .then((rows) => { if (!cancelled) setHistory(rows); })
      .catch(() => { /* history is best-effort */ });
    return () => { cancelled = true; };
  }, [currentTenant, proposalId]);

  const canGenerate = Number(budget) > 0 && title.trim().length > 0;
  const nums = (s: string) => s.split(',').map((x) => Number(x.trim())).filter((n) => n > 0);

  function buildInput(): ReportInput {
    const investigation = includeAudit
      ? {
          intro: auditIntro,
          rows: auditRows
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
            .map((l) => {
              const i = l.indexOf(':');
              return i > 0
                ? { label: l.slice(0, i).trim(), finding: l.slice(i + 1).trim() }
                : { label: '', finding: l };
            }),
          diagnosis: auditDiagnosis,
        }
      : undefined;
    return {
      title,
      subtitle,
      intakeBudgetUsd: Number(budget),
      cvr: Number(cvr) / 100,
      closeRate: Number(closeRate) / 100,
      jobValue: Number(jobValue),
      cpcs: nums(cpc),
      cpcMode,
      planningCpcs: cpcMode === 'manual' ? nums(planningCpcs) : undefined,
      cpcBase: cpcMode === 'formula' ? Number(cpcBase) : undefined,
      cpcDropRate: cpcMode === 'formula' ? Number(cpcDropRate) / 100 : undefined,
      metaMode,
      brand: brand ?? undefined,
      mode,
      paper: paper || undefined,
      font,
      website: website.trim() || undefined,
      logoLabel: logoLabel.trim() || undefined,
      logoUrl: logoUrl.trim() || undefined,
      tiles: tiles.length ? tiles : undefined,
      recommendationPill: recPill.trim() || undefined,
      readText,
      recommendationText,
      investigation,
    };
  }

  // AI-draft one or more fields, grounded on the intake + prospect site.
  function suggestContext(): Record<string, unknown> {
    return {
      title,
      service_industry: title,
      intake_budget_usd: Number(budget),
      cvr: Number(cvr) / 100,
      close_rate: Number(closeRate) / 100,
      job_value: Number(jobValue),
    };
  }

  // Existing client → tenant = the site's domain (enables the live campaign read).
  function tenantFromWebsite(): string | undefined {
    const d = website.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');
    return d || undefined;
  }

  // Pre-warm the grounding when the website is entered so the first ✨ is already fast.
  async function warmGrounding() {
    const site = website.trim();
    if (!site) return;
    const key = `${site}|${title}`;
    if (warmedKeyRef.current === key) return; // already warmed this site/industry
    warmedKeyRef.current = key;
    setWarmState('warming');
    try {
      const info = await proposalService.warmGrounding(site, tenantFromWebsite(), suggestContext());
      setWarmInfo({ keywords: info.keywords, campaigns: info.campaigns });
      setWarmState('ready');
    } catch {
      setWarmState('error');
      warmedKeyRef.current = ''; // let a later blur retry
    }
  }

  const setters: Record<string, (v: string) => void> = {
    title: setTitle,
    subtitle: setSubtitle,
    read_text: setReadText,
    recommendation_text: setRecommendationText,
    recommendation_pill: setRecPill,
  };

  const SUGGEST_FAIL = 'AI draft unavailable — no model configured, or the site could not be read. Write the fields by hand.';

  // "Draft all" — one coherent pass; apply the first (only) option of each field.
  async function draftAll() {
    setSuggesting('all');
    setError(null);
    try {
      const { options } = await proposalService.suggestFields(
        ALL_NARRATIVE, suggestContext(), website.trim() || undefined, tenantFromWebsite(), 1,
      );
      const first = (f: string) => (Array.isArray(options[f]) ? (options[f] as string[])[0] : undefined);
      for (const f of Object.keys(setters)) {
        const v = first(f);
        if (typeof v === 'string') setters[f](v);
      }
      if (Array.isArray(options.tiles)) setTiles(options.tiles as { k: string; v: string }[]);
    } catch {
      setError(SUGGEST_FAIL);
    } finally {
      setSuggesting(null);
    }
  }

  async function fetchColors() {
    if (!website.trim()) return;
    setBrandLoading(true);
    setError(null);
    try {
      const palette = await proposalService.extractBrandPalette(website.trim());
      setBrand(palette.brand);
      setSwatches(palette.swatches);
      setPrimaryHex(palette.primary);
      setAccentHex(palette.accent);
      if (palette.logo) setLogoUrl(palette.logo); // auto-fill the prospect logo
    } catch {
      setError('Could not read brand colors from that site — pick them by hand or generate with the default theme.');
    } finally {
      setBrandLoading(false);
    }
  }

  // Apply a primary+accent pair → re-derive the full theme (backend keeps one
  // derivation, so manual pickers, presets, swatches and auto never diverge).
  async function applyRoles(primary: string, accent: string) {
    setPrimaryHex(primary);
    setAccentHex(accent);
    try {
      const palette = await proposalService.deriveTheme(primary, accent);
      setBrand(palette.brand);
    } catch {
      setError('Could not apply that color.');
    }
  }

  function assignSwatch(hex: string) {
    const primary = assignRole === 'primary' ? hex : (primaryHex ?? DEFAULT_PRIMARY);
    const accent = assignRole === 'accent' ? hex : (accentHex ?? DEFAULT_ACCENT);
    void applyRoles(primary, accent);
  }

  async function generate() {
    setLoading(true);
    setError(null);
    try {
      const { html } = await proposalService.generateReport(buildInput());
      setReportHtml(html);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  }

  // Current preview HTML (reflects any inline edits), or null if nothing rendered.
  function currentPreviewHtml(): string | null {
    const doc = frameRef.current?.contentDocument;
    if (reportHtml && doc?.documentElement) return '<!doctype html>' + doc.documentElement.outerHTML;
    return reportHtml;
  }

  async function downloadPdf() {
    setLoading(true);
    setError(null);
    try {
      // Prefer the CURRENT preview HTML so inline edits flow into the PDF; else rebuild.
      const html = currentPreviewHtml();
      const blob = html
        ? await proposalService.renderPdfFromHtml(html, title)
        : await proposalService.generateReportPdf(buildInput());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'proposal.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Server renderer unavailable (503) → fall back to the browser print dialog.
      setError('Server PDF renderer unavailable — using browser Print / Save as PDF instead.');
      frameRef.current?.contentWindow?.print();
    } finally {
      setLoading(false);
    }
  }

  // ---- inline editing of the rendered preview -----------------------------
  function startEdit() {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.body) return;
    doc.body.contentEditable = 'true';
    doc.body.style.outline = 'none';
    doc.designMode = 'on';
    setEditing(true);
    doc.body.focus();
  }

  function saveEdits() {
    const doc = frameRef.current?.contentDocument;
    if (doc?.body) {
      doc.designMode = 'off';
      doc.body.contentEditable = 'false';
      setReportHtml('<!doctype html>' + doc.documentElement.outerHTML); // persist edits into state
    }
    setEditing(false);
  }

  function cancelEdits() {
    setEditing(false);
    // Reload the iframe from the last saved HTML to discard unsaved DOM edits.
    if (frameRef.current && reportHtml) frameRef.current.srcdoc = reportHtml;
  }

  // Per-field AI suggest — the SHARED app-wide <AISuggest> component (text/rewrite
  // mode), wired to our grounded backend via a custom fetcher so the UI is uniform
  // with the rest of the app while keeping the site + keyword + account grounding.
  const fieldSuggest = (fieldKey: string, sectionId: string) => (
    <div className="mt-1 flex justify-start" data-testid={`suggest-${fieldKey}`}>
      <AISuggest
        kind="text"
        sectionId={sectionId}
        placeholder="Suggest"
        source="site + keywords + account"
        onAcceptText={(t) => setters[fieldKey]?.(t)}
        fetcher={async () => {
          const { options, groundedOn } = await proposalService.suggestFields(
            [fieldKey], suggestContext(), website.trim() || undefined, tenantFromWebsite(), 3,
          );
          const items = Array.isArray(options[fieldKey]) ? (options[fieldKey] as string[]) : [];
          return { items, source: groundedOn.length ? groundedOn.join(' + ') : 'AI' };
        }}
      />
    </div>
  );

  const ALL_NARRATIVE = ['title', 'subtitle', 'read_text', 'recommendation_text', 'recommendation_pill', 'tiles'];

  return (
    <div className="mx-auto flex h-[calc(100vh-11rem)] max-w-6xl flex-col" data-testid="proposals-page">
      <div className="mb-4 flex shrink-0 items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-coral">
            Agile &amp; Co · Proposal Studio
          </div>
          <h1 className="font-display text-4xl leading-none text-foreground">Audit → Proposal</h1>
          <p className="mt-2.5 max-w-[52ch] text-sm text-muted-foreground">
            Intake → live benchmark forecast → a branded budget proposal, ready to send. ✨ drafts fields from the prospect's site.
          </p>
        </div>
        <button
          type="button"
          data-testid="draft-all-btn"
          disabled={suggesting !== null}
          onClick={() => draftAll()}
          className="shrink-0 rounded-lg bg-coral px-4 py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(10,118,168,0.3)] transition-colors hover:bg-coral-hover disabled:opacity-50"
        >
          {suggesting === 'all' ? 'Drafting…' : '✦ Draft all'}
        </button>
      </div>

      {/* Fixed-height body: each pane scrolls on its own so the page never scrolls. */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,380px)_1fr]">
        {/* ---------- controls (scrolls; actions pinned) ---------- */}
        <div className="flex min-h-0 min-w-0 flex-col">
          <div className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden pb-1 pr-1">
          {history.length > 0 && (
            <Section id="recent" title="Recent proposals">
              <ul className="space-y-0.5" data-testid="proposal-history">
                {history.slice(0, 12).map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/proposals/${p.id}`)}
                      title={p.title}
                      className={`flex w-full items-center justify-between gap-2 rounded-lg border-l-2 px-2.5 py-2 text-left text-[13px] transition-colors ${
                        p.id === proposalId
                          ? 'border-coral bg-coral-light text-coral-dark'
                          : 'border-transparent text-foreground hover:bg-paper-2'
                      }`}
                    >
                      <span className="min-w-0 truncate">{p.title || p.prospectKey}</span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {new Date(p.createdAt).toLocaleDateString()}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          <Section id="intake" title="Intake">
            <label className={label}>Title
              <input data-testid="intake-title" value={title} onChange={(e) => setTitle(e.target.value)} className={field} />
            </label>
            {fieldSuggest('title', 'proposal-title')}
            <label className={label}>Subtitle
              <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className={field} />
            </label>
            {fieldSuggest('subtitle', 'proposal-subtitle')}
            <div className="grid grid-cols-2 gap-3">
              <label className={label}>Monthly budget ($)
                <input data-testid="intake-budget" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className={field} />
              </label>
              <label className={label}>Avg job value ($)
                <input type="number" value={jobValue} onChange={(e) => setJobValue(e.target.value)} className={field} />
              </label>
              <label className={label}>Conversion rate (%)
                <input type="number" value={cvr} onChange={(e) => setCvr(e.target.value)} className={field} />
              </label>
              <label className={label}>Close rate (%)
                <input type="number" value={closeRate} onChange={(e) => setCloseRate(e.target.value)} className={field} />
              </label>
            </div>
            <label className={label}>Keyword CPCs, comma-separated (for the band)
              <input value={cpc} onChange={(e) => setCpc(e.target.value)} className={field} />
            </label>
          </Section>

          <Section id="brand" title="Brand">
            <div className="grid grid-cols-2 gap-3">
              <label className={label}>Prospect website
                <input
                  data-testid="brand-website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  onBlur={warmGrounding}
                  placeholder="example.com"
                  className={field}
                />
              </label>
              <label className={label}>Report label
                <input value={logoLabel} onChange={(e) => setLogoLabel(e.target.value)} className={field} />
              </label>
            </div>
            <label className={label}>Prospect logo URL
              <div className="flex items-center gap-2">
                <input
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="auto-filled on Fetch — or paste a URL (blank = text label)"
                  className={field}
                />
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="logo preview"
                    className="h-9 max-w-[72px] shrink-0 rounded bg-white object-contain p-1"
                  />
                ) : null}
              </div>
            </label>
            {warmState !== 'idle' && (
              <div data-testid="warm-status" className="mt-1 text-[11px] text-muted-foreground">
                {warmState === 'warming' && <span className="text-teal-dark">⟳ Preparing research… (so AI suggestions are instant)</span>}
                {warmState === 'ready' && warmInfo && (
                  <span className="text-teal-dark">
                    ✓ Research ready · {warmInfo.keywords} keywords{warmInfo.campaigns > 0 ? ` · ${warmInfo.campaigns} live campaigns` : ''}
                  </span>
                )}
                {warmState === 'error' && <span>Couldn’t pre-load research — AI suggest still works (just slower first time).</span>}
              </div>
            )}
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                data-testid="fetch-colors-btn"
                disabled={!website.trim() || brandLoading}
                onClick={fetchColors}
                className="rounded-lg border border-warm-border-strong px-3.5 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-coral hover:text-coral disabled:opacity-50"
              >
                {brandLoading ? 'Reading site…' : 'Fetch brand colors'}
              </button>
              {brand && (
                <button
                  type="button"
                  data-testid="clear-brand-btn"
                  onClick={() => { setBrand(null); setSwatches([]); setPrimaryHex(null); setAccentHex(null); }}
                  className="text-xs text-muted-foreground underline hover:text-foreground"
                >
                  Reset to default theme
                </button>
              )}
            </div>

            {/* Manual color scheme: pickers + presets (independent of a fetch) */}
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Primary
                <input
                  type="color"
                  data-testid="primary-picker"
                  value={primaryHex ?? DEFAULT_PRIMARY}
                  onChange={(e) => applyRoles(e.target.value, accentHex ?? DEFAULT_ACCENT)}
                  className="h-6 w-8 cursor-pointer rounded border border-border bg-transparent p-0"
                />
              </label>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Accent
                <input
                  type="color"
                  data-testid="accent-picker"
                  value={accentHex ?? DEFAULT_ACCENT}
                  onChange={(e) => applyRoles(primaryHex ?? DEFAULT_PRIMARY, e.target.value)}
                  className="h-6 w-8 cursor-pointer rounded border border-border bg-transparent p-0"
                />
              </label>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Presets:</span>
                {COLOR_PRESETS.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    title={`${p.name} — ${p.primary} / ${p.accent}`}
                    onClick={() => applyRoles(p.primary, p.accent)}
                    className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-warm-bg"
                  >
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: p.primary }} />
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: p.accent }} />
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            {/* Report mode + page/PDF background color */}
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>Mode:</span>
                {(['light', 'dark'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`mode-${m}`}
                    onClick={() => { setMode(m); setPaper(''); }}
                    className={`rounded-full px-2 py-0.5 capitalize ${
                      mode === m ? 'bg-coral text-white' : 'border border-border text-muted-foreground'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                PDF background
                <input
                  type="color"
                  data-testid="paper-picker"
                  value={paper || (mode === 'dark' ? '#0f1115' : '#ffffff')}
                  onChange={(e) => setPaper(e.target.value)}
                  className="h-6 w-8 cursor-pointer rounded border border-border bg-transparent p-0"
                />
                {paper && (
                  <button type="button" onClick={() => setPaper('')} className="underline hover:text-foreground">
                    reset
                  </button>
                )}
              </label>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Font
                <select
                  data-testid="font-select"
                  value={font}
                  onChange={(e) => setFont(e.target.value)}
                  className="rounded border border-border bg-paper px-1.5 py-0.5 text-xs text-foreground"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.key} value={f.key}>{f.label}</option>
                  ))}
                </select>
              </label>
            </div>

            {brand && (
              <div className="mt-3" data-testid="brand-swatches">
                {/* which role the next swatch click assigns */}
                <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Click a swatch to set:</span>
                  {(['primary', 'accent'] as const).map((role) => (
                    <button
                      key={role}
                      type="button"
                      data-testid={`role-${role}`}
                      onClick={() => setAssignRole(role)}
                      className={`rounded-full px-2 py-0.5 capitalize ${
                        assignRole === role ? 'bg-coral text-white' : 'border border-border text-muted-foreground'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {swatches.map((c) => {
                    const active = c === primaryHex || c === accentHex;
                    return (
                      <button
                        key={c}
                        type="button"
                        title={`${c} — set as ${assignRole}`}
                        onClick={() => assignSwatch(c)}
                        style={{ backgroundColor: c }}
                        className={`h-7 w-7 rounded ${
                          active ? 'ring-2 ring-offset-1 ring-foreground' : 'border border-border hover:ring-2 hover:ring-coral'
                        }`}
                      />
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: brand.navy }} /> primary {brand.navy}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: brand.rust }} /> accent {brand.rust}
                  </span>
                </div>
              </div>
            )}
          </Section>

          <Section id="model" title="Model">
            <label className={label}>CPC mode
              <select data-testid="cpc-mode" value={cpcMode} onChange={(e) => setCpcMode(e.target.value as CpcMode)} className={field}>
                <option value="auto">Auto — single P75 (constant, spec)</option>
                <option value="manual">Manual — type per-tier CPC</option>
                <option value="formula">Formula — drop % per tier</option>
              </select>
            </label>
            {cpcMode === 'manual' && (
              <label className={label}>Per-tier CPCs (conservative, moderate, aggressive)
                <input data-testid="planning-cpcs" value={planningCpcs} onChange={(e) => setPlanningCpcs(e.target.value)} className={field} />
              </label>
            )}
            {cpcMode === 'formula' && (
              <div className="grid grid-cols-2 gap-3">
                <label className={label}>Base CPC ($)
                  <input type="number" value={cpcBase} onChange={(e) => setCpcBase(e.target.value)} className={field} />
                </label>
                <label className={label}>Drop per tier (%)
                  <input type="number" value={cpcDropRate} onChange={(e) => setCpcDropRate(e.target.value)} className={field} />
                </label>
              </div>
            )}
            <label className={label}>Meta / PMax
              <select data-testid="meta-mode" value={metaMode} onChange={(e) => setMetaMode(e.target.value as MetaMode)} className={field}>
                <option value="in_plan">In the plan (channel split)</option>
                <option value="advisory">Advisory only (Google + LSA)</option>
              </select>
            </label>
          </Section>

          <Section id="narrative" title="Narrative · Gate B (human)">
            <label className={label}>The Read
              <textarea value={readText} onChange={(e) => setReadText(e.target.value)} rows={4} className={field} />
            </label>
            {fieldSuggest('read_text', 'proposal-read')}
            <label className={label}>The Recommendation
              <textarea value={recommendationText} onChange={(e) => setRecommendationText(e.target.value)} rows={4} className={field} />
            </label>
            {fieldSuggest('recommendation_text', 'proposal-rec')}
            {tiles.length > 0 && (
              <div className="mt-2" data-testid="drafted-tiles">
                <div className="text-xs text-muted-foreground">Drafted quick-facts (appear in The Read):</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {tiles.map((t, i) => (
                    <span key={i} className="rounded bg-warm-bg px-2 py-0.5 text-xs text-foreground">
                      <b>{t.k}:</b> {t.v}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>

          <Section id="investigation" title="Investigation · account audit (optional)">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input data-testid="include-audit" type="checkbox" checked={includeAudit} onChange={(e) => setIncludeAudit(e.target.checked)} />
              Include audit section (when the prospect already has an account)
            </label>
            {includeAudit && (
              <div className="mt-2 space-y-2">
                <label className={label}>Intro
                  <textarea value={auditIntro} onChange={(e) => setAuditIntro(e.target.value)} rows={2} className={field} />
                </label>
                <label className={label}>Findings — one “Label: finding” per line
                  <textarea value={auditRows} onChange={(e) => setAuditRows(e.target.value)} rows={5} className={field} />
                </label>
                <label className={label}>Diagnosis
                  <textarea value={auditDiagnosis} onChange={(e) => setAuditDiagnosis(e.target.value)} rows={3} className={field} />
                </label>
              </div>
            )}
          </Section>

          </div>

          {/* pinned actions — always visible below the scrolling controls */}
          <div className="mt-3 shrink-0 border-t border-warm-border pt-3">
            <div className="flex items-center gap-2.5">
              <button
                data-testid="generate-btn"
                disabled={!canGenerate || loading}
                onClick={generate}
                className="rounded-lg bg-coral px-4 py-2 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(10,118,168,0.3)] transition-colors hover:bg-coral-hover disabled:opacity-50"
              >
                {loading ? 'Generating…' : 'Generate report'}
              </button>
              <button
                data-testid="download-pdf-btn"
                disabled={!canGenerate || loading}
                onClick={downloadPdf}
                className="rounded-lg border border-warm-border-strong px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:border-coral hover:text-coral disabled:opacity-50"
              >
                Download PDF
              </button>
              {reportHtml && (
                <button
                  data-testid="print-btn"
                  onClick={() => frameRef.current?.contentWindow?.print()}
                  className="rounded-lg border border-warm-border px-3.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-paper-2"
                >
                  Print
                </button>
              )}
            </div>
            {error && <p data-testid="error" className="mt-2 text-sm text-destructive">{error}</p>}
          </div>
        </div>

        {/* ---------- report preview (fills height; scrolls inside the iframe) ---------- */}
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-warm-border">
          {reportHtml && (
            <div className="flex shrink-0 items-center justify-between border-b border-warm-border bg-paper px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                Preview{editing ? ' · editing — click text to change it' : ''}
              </span>
              <div className="flex items-center gap-2">
                {editing ? (
                  <>
                    <button
                      type="button"
                      data-testid="save-edit-btn"
                      onClick={saveEdits}
                      className="rounded bg-coral px-2 py-0.5 text-white hover:bg-coral-hover"
                    >
                      Save
                    </button>
                    <button type="button" onClick={cancelEdits} className="text-muted-foreground underline hover:text-foreground">
                      cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    data-testid="edit-btn"
                    onClick={startEdit}
                    className="rounded border border-border px-2 py-0.5 text-foreground hover:bg-warm-bg"
                  >
                    ✎ Edit
                  </button>
                )}
              </div>
            </div>
          )}
          <div
            className="min-h-0 flex-1 p-5"
            style={{
              background:
                'radial-gradient(640px 240px at 50% 0%, color-mix(in srgb, var(--color-coral) 12%, transparent), transparent 62%), var(--color-paper-2)',
            }}
          >
            {reportHtml ? (
              <iframe
                ref={frameRef}
                data-testid="report-frame"
                title="Proposal report"
                srcDoc={reportHtml}
                className={`h-full w-full rounded-xl border border-warm-border bg-white shadow-2xl ${editing ? 'ring-2 ring-inset ring-coral' : ''}`}
              />
            ) : (
              <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-warm-border-strong bg-paper/60 px-6 text-center text-sm text-muted-foreground">
                <span>
                  Fill the intake and hit <span className="font-medium text-coral">Generate report</span> to preview the branded proposal.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
